import { useMemo, useState, type JSX } from 'react'
import type { VariableDef } from '../../../hooks/useEventVariables.js'
import { useTemplatePreview } from '../../../hooks/useTemplatePreview.js'

export type PreviewChannel = 'email' | 'sms' | 'in_app' | 'push'

export interface LivePreviewProps {
  /** Subject template — only meaningful for email/push channels. */
  subjectTemplate: string | null
  /** Body template, rendered with the current sample data. */
  bodyTemplate: string
  /** Channels selected for this template; drives the per-channel tabs. */
  channels: PreviewChannel[]
  /** Variable schema; `example` values seed the default sample data. */
  variables: VariableDef[]
}

/** Channels whose preview renders the body as HTML. */
const HTML_CHANNELS: ReadonlySet<PreviewChannel> = new Set(['email'])
/** Channels that show a subject line. */
const SUBJECT_CHANNELS: ReadonlySet<PreviewChannel> = new Set(['email', 'push'])

const CHANNEL_LABEL: Record<PreviewChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  in_app: 'In-app',
  push: 'Push',
}

// --- HTML sanitizer --------------------------------------------------------

/** Tags allowed in sanitized email-preview HTML. */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'span', 'div',
  'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'blockquote',
  'pre', 'code', 'hr', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
])
/** Tags removed entirely, including their text content. */
const STRIP_WITH_CONTENT: ReadonlySet<string> = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template',
])
/** Attributes permitted on surviving elements. */
const ALLOWED_ATTRS: ReadonlySet<string> = new Set(['href', 'src', 'alt', 'title'])

/** Returns true if a URL attribute value is safe (no javascript:/data: scheme). */
function isSafeUrl(value: string): boolean {
  // Browsers strip tab/CR/LF and C0 control chars (incl. NUL) when resolving a
  // URL scheme, so e.g. `java&#9;script:` resolves to `javascript:`. Strip those
  // characters before comparing so they cannot smuggle a dangerous scheme past
  // the prefix checks below.
  // eslint-disable-next-line no-control-regex -- intentionally matches C0 control chars and DEL to strip URL-smuggling characters (e.g. tab-encoded javascript: scheme)
  const v = value.replace(/[\x00-\x20\x7f]/g, '').trim().toLowerCase()
  // Block dangerous schemes; allow http(s), mailto, relative, anchors.
  return (
    !v.startsWith('javascript:') &&
    !v.startsWith('data:') &&
    !v.startsWith('vbscript:')
  )
}

/**
 * Sanitizes untrusted HTML for the email preview using a strict allowlist.
 * Strips `<script>`/`<style>`/`<iframe>`/`<object>`/`<embed>` (with content),
 * unwraps any other non-allowlisted tag, removes every `on*` event-handler
 * attribute, drops non-allowlisted attributes, and neutralizes
 * `javascript:`/`data:` URLs on `href`/`src`. Security-sensitive: the only
 * value passed to `dangerouslySetInnerHTML`.
 */
export function sanitizeHtml(html: string): string {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // No DOM available — fall back to escaping everything.
    return html.replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }
  const doc = new DOMParser().parseFromString(html, 'text/html')

  function clean(node: Node): void {
    // Snapshot children since we mutate the live list while iterating.
    const children = Array.from(node.childNodes)
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element
        const tag = el.tagName.toLowerCase()
        if (STRIP_WITH_CONTENT.has(tag)) {
          el.remove()
          continue
        }
        if (!ALLOWED_TAGS.has(tag)) {
          // Unwrap: keep (cleaned) children, drop the tag itself.
          clean(el)
          const parent = el.parentNode
          if (parent) {
            while (el.firstChild) parent.insertBefore(el.firstChild, el)
            parent.removeChild(el)
          }
          continue
        }
        // Allowed tag: scrub attributes.
        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase()
          if (name.startsWith('on') || !ALLOWED_ATTRS.has(name)) {
            el.removeAttribute(attr.name)
            continue
          }
          if ((name === 'href' || name === 'src') && !isSafeUrl(attr.value)) {
            el.removeAttribute(attr.name)
          }
        }
        clean(el)
      }
    }
  }

  clean(doc.body)
  return doc.body.innerHTML
}

// --- Component -------------------------------------------------------------

/** Builds the default sample-data record from the variable schema examples. */
function defaultSampleData(variables: VariableDef[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const v of variables) out[v.name] = v.example
  return out
}

/**
 * Live notification-template preview. Renders the template against editable
 * sample data via {@link useTemplatePreview}, with per-channel tabs (SMS/in-app
 * as plain text, email as sanitized HTML) and an "Edit sample data" toggle.
 */
export default function LivePreview({
  subjectTemplate,
  bodyTemplate,
  channels,
  variables,
}: LivePreviewProps): JSX.Element {
  const tabs: [PreviewChannel, ...PreviewChannel[]] =
    channels.length > 0
      ? (channels as [PreviewChannel, ...PreviewChannel[]])
      : ['in_app']
  const [activeTab, setActiveTab] = useState<PreviewChannel>(tabs[0])
  const [editingSample, setEditingSample] = useState(false)
  const [sampleJson, setSampleJson] = useState<string>(() =>
    JSON.stringify(defaultSampleData(variables), null, 2),
  )
  const [sampleError, setSampleError] = useState<string | null>(null)

  // Parse the sample-data editor; fall back to schema defaults on bad JSON.
  const sampleData = useMemo<Record<string, unknown>>(() => {
    try {
      const parsed = JSON.parse(sampleJson) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
      return {}
    } catch {
      return {}
    }
  }, [sampleJson])

  const active = tabs.includes(activeTab) ? activeTab : tabs[0]

  const { subject, body, loading } = useTemplatePreview({
    subjectTemplate,
    bodyTemplate,
    variables: sampleData,
  })

  const showSubject = SUBJECT_CHANNELS.has(active)
  const renderAsHtml = HTML_CHANNELS.has(active)

  function onSampleChange(value: string): void {
    setSampleJson(value)
    try {
      const parsed = JSON.parse(value) as unknown
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setSampleError('Sample data must be a JSON object.')
      } else {
        setSampleError(null)
      }
    } catch (e) {
      setSampleError(`Invalid JSON: ${(e as Error).message}`)
    }
  }

  return (
    <section
      aria-label="Template preview"
      className="lp flex flex-col gap-2 rounded border border-gray-300 p-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Live preview</h3>
        <button
          type="button"
          className="text-xs text-blue-600 underline"
          aria-pressed={editingSample}
          onClick={() => setEditingSample((v) => !v)}
        >
          {editingSample ? 'Hide sample data' : 'Edit sample data'}
        </button>
      </div>

      {editingSample && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600" htmlFor="lp-sample">
            Sample data (JSON)
          </label>
          <textarea
            id="lp-sample"
            aria-label="Sample data JSON"
            className="h-32 rounded border border-gray-300 p-2 font-mono text-xs"
            value={sampleJson}
            onChange={(e) => onSampleChange(e.target.value)}
          />
          {sampleError && (
            <p role="alert" className="text-xs text-red-600">
              {sampleError}
            </p>
          )}
        </div>
      )}

      {tabs.length > 1 && (
        <div role="tablist" aria-label="Channel preview" className="flex gap-1">
          {tabs.map((ch) => (
            <button
              key={ch}
              type="button"
              role="tab"
              aria-selected={ch === active}
              className={
                ch === active
                  ? 'rounded-t border-b-2 border-blue-600 px-3 py-1 text-xs font-medium'
                  : 'rounded-t px-3 py-1 text-xs text-gray-500'
              }
              onClick={() => setActiveTab(ch)}
            >
              {CHANNEL_LABEL[ch]}
            </button>
          ))}
        </div>
      )}

      <div
        aria-live="polite"
        role="tabpanel"
        data-testid="lp-panel"
        data-channel={active}
        className="flex flex-col gap-2 rounded bg-gray-50 p-3"
      >
        {showSubject && (
          <div className="border-b border-gray-200 pb-2">
            <span className="text-xs uppercase text-gray-500">Subject</span>
            <p data-testid="lp-subject" className="text-sm font-medium">
              {subject ?? ''}
            </p>
          </div>
        )}
        {loading ? (
          <p className="text-xs text-gray-400">Rendering…</p>
        ) : renderAsHtml ? (
          <div
            data-testid="lp-body-html"
            className="text-sm"
            // Sanitized above — sanitizeHtml strips scripts/handlers/js: URLs.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
          />
        ) : (
          <pre
            data-testid="lp-body-text"
            className="whitespace-pre-wrap break-words text-sm"
          >
            {body}
          </pre>
        )}
      </div>
    </section>
  )
}
